/**
 * Home page / Landing page
 */

import { Link } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import {
  ShieldCheckIcon,
  BoltIcon,
  ChartBarIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import { useGetSoftwareListQuery } from '@/services/api';

export default function HomePage() {
  const { isSignedIn } = useAuth();
  const { data: softwareData } = useGetSoftwareListQuery({ limit: 6 });

  const features = [
    {
      icon: ShieldCheckIcon,
      title: 'Automated Tracking',
      description: 'Track 44+ enterprise software products with automated version discovery and patch monitoring.',
    },
    {
      icon: BoltIcon,
      title: 'Real-time Updates',
      description: 'Get instant notifications when new versions or security patches are released.',
    },
    {
      icon: ChartBarIcon,
      title: 'CVE Integration',
      description: 'Automatically link software versions to known vulnerabilities from NVD database.',
    },
    {
      icon: LockClosedIcon,
      title: 'Secure Downloads',
      description: 'Download verified binaries with SHA-256 validation and secure signed URLs.',
    },
  ];

  return (
    <div className="space-y-16">
      {/* Hero section */}
      <section className="text-center py-12 md:py-20">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyber-blue-500/10 border border-cyber-blue-500/20 rounded-full mb-8">
          <div className="w-2 h-2 bg-cyber-blue-500 rounded-full animate-pulse" />
          <span className="text-sm text-cyber-blue-400 font-medium">
            Tracking 44 software products
          </span>
        </div>

        <h1 className="text-4xl md:text-6xl font-bold mb-6">
          Enterprise Patch
          <span className="text-gradient"> Management</span>
        </h1>

        <p className="text-xl text-gray-400 mb-8 max-w-3xl mx-auto">
          Automated software version tracking, vulnerability monitoring, and patch management
          for enterprise environments. Stay ahead of security threats with real-time updates.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/catalog" className="btn-primary text-lg px-8 py-3">
            Browse Software Catalog
          </Link>
          {!isSignedIn && (
            <Link to="/sign-up" className="btn-secondary text-lg px-8 py-3">
              Get Started
            </Link>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12 max-w-4xl mx-auto">
          {[
            { label: 'Software Products', value: '44+' },
            { label: 'Versions Tracked', value: '10K+' },
            { label: 'CVEs Monitored', value: '50K+' },
            { label: 'Daily Scans', value: '200+' },
          ].map((stat) => (
            <div key={stat.label} className="card text-center">
              <div className="text-3xl font-bold text-cyber-blue-500 mb-2">{stat.value}</div>
              <div className="text-sm text-gray-400">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section>
        <h2 className="text-3xl font-bold text-center mb-12">
          Why Choose <span className="text-gradient">Patch Manager</span>
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature) => (
            <div key={feature.title} className="card-glow group">
              <feature.icon className="w-10 h-10 text-cyber-blue-500 mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
              <p className="text-gray-400 text-sm">{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Recent software */}
      {softwareData && softwareData.data.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl font-bold">Featured Software</h2>
            <Link to="/catalog" className="link text-sm">
              View all →
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {softwareData.data.map((software) => (
              <Link
                key={software.id}
                to={`/software/${software.id}`}
                className="card-glow group"
              >
                <div className="flex items-start gap-4">
                  {software.icon_url ? (
                    <img
                      src={software.icon_url}
                      alt={software.name}
                      className="w-12 h-12 rounded-lg"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-cyber-blue-500/10 rounded-lg flex items-center justify-center">
                      <ShieldCheckIcon className="w-6 h-6 text-cyber-blue-500" />
                    </div>
                  )}

                  <div className="flex-1">
                    <h3 className="font-semibold mb-1 group-hover:text-cyber-blue-400 transition-colors">
                      {software.name}
                    </h3>
                    <p className="text-sm text-gray-400 mb-2">{software.manufacturer}</p>
                    <div className="flex items-center gap-2">
                      <span className="badge badge-info text-xs">{software.category}</span>
                      {software.quantum_ready_status === 'ready' && (
                        <span className="badge bg-green-500/10 text-green-400 border border-green-500/20 text-xs">
                          Quantum Ready
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* CTA section */}
      <section className="card-glow text-center py-12">
        <h2 className="text-3xl font-bold mb-4">Ready to get started?</h2>
        <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
          Join organizations using Patch Manager to stay secure and compliant with
          automated patch tracking and vulnerability monitoring.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          {!isSignedIn && (
            <Link to="/sign-up" className="btn-primary px-8 py-3">
              Create Free Account
            </Link>
          )}
          <Link to="/catalog" className="btn-secondary px-8 py-3">
            Explore Software
          </Link>
        </div>
      </section>
    </div>
  );
}
